import { ReactComponent as IconCalendar } from "@/assets/svgs/calendar.svg";
import classname from "classnames";
import moment from "moment";
import { forwardRef, useEffect, useState } from "react";
import ReactDatePicker from "react-datepicker";
import Dropdown from "@/components/Dropdown/index";
import { ReactComponent as IconCloseGrey } from "@/assets/svgs/iconCloseGrey.svg";
import PropTypes from 'prop-types';
import InputWithTooltipPlaceholder from './InputWithTooltipPlaceholder';

function getClassName(error) {
	return classname({
		"eform-input": true,
		"eform-input-standard": true,
		"eform-input-error": error ? true : false,
	});
}

const CustomInput = forwardRef((props, ref) => {
	function getClassName(props) {
		return classname({
			...props.className,
			"eform-input": true,
			"eform-input-standard": true,
			"eform-custom-date-picker-empty": !props.value,
			"eform-input-error": !!props.error,
			"eform-input-standard-disabled": !!props.disabled,
		});
	}
	const [inputValue, setInputValue] = useState(props.value);

	useEffect(() => {
		setInputValue(props.value);
	}, [props.value]);

	function handleInputChange(e) {
		const value = e.target.value;
		setInputValue(value);
		props.customInputChange(value);
	}

	if (inputValue) {
		return (
			<input
				data-testid="eform-custom-date-picker"
				{...props}
				value={inputValue}
				onChange={handleInputChange}
				className={getClassName(props)}
				ref={ref}
			/>
		);
	}

	return (
		<div
			data-testid="eform-custom-date-picker-empty"
			{...props}
			className={getClassName(props)}
			ref={ref}
			tabIndex="0"
		>
			{props?.value && <IconCalendar />}
			<div className="ml-1 font-color-grey-60">{props.placeholder}</div>
		</div>
	);
});

const CellStringComponent = ({
	error,
	isReadOnly,
	schema,
	payload,
	parentProps,
	inputProps,
	setInputProps,
	updatePayload,
	validateSchema,
	getFieldValue,
	setEFormFieldError,
	setIsValidFormat,
	required,
	propertyName
}) => {

	const options = () => {
		if (schema.enum) {
			return schema.enum.map((v, index) => {
				return { value: v, label: v, index: index }
			})
		}
	}
	useEffect(() => {
		setInputProps({
			...inputProps,
			disabled: isReadOnly,
			placeholder: isReadOnly ? null : schema.description || schema.title || "",
			value: getFieldValue(payload, parentProps) === null ? "" : getFieldValue(payload, parentProps),
			onChange,
		});
	}, [payload, isReadOnly]);

	const [selectedDate, setSelectedDate] = useState(null);

	function validDateTimeFormat(value) {
		let validateResult;
		if (schema.format === "date-time") {
			const UTCFormat = "YYYY-MM-DDTHH:mm:ss.SSS[Z]"
			validateResult = moment(value, UTCFormat, true).isValid();
		} else if (schema.format === "date") {
			validateResult = moment(value, "YYYY-MM-DD", true).isValid();
		} else if (schema.format?.startsWith("custom:date-time:")) {
			const customFormat = schema.format.replace("custom:date-time:", "");
			validateResult = moment(value, customFormat, true).isValid();
		}
		setEFormFieldError(!validateResult);
		setIsValidFormat(validateResult);
		return validateResult;
	}

	function customInputChange(value) {
		updatePayload(value);
		validateSchema(value);
		validDateTimeFormat(value);
	}

	function onChange(e) {
		if (isReadOnly) {
			return;
		}
		let value = e?.target?.value;
		if (e?.target) {
			value = e.target.value;
		} else if (schema.format === "date-time") {
			// const updatedDate = new Date(e).getTime() - new Date(e).getTimezoneOffset() * 60000;
			value = moment(e).toISOString();
		} else if (schema.format === "date") {
			value = moment(e).format().split("T")[0];
		} else if (schema.format?.startsWith("custom:date-time:")) {
			const customFormat = schema.format.replace("custom:date-time:", "");
			value = moment(e).format(customFormat);
		}
		setSelectedDate(e);
		setInputProps({ ...inputProps, value });
		updatePayload(value);
		validateSchema(value);
	}
	function onClick(e) {
		if (isReadOnly) {
			return;
		}
		let value = e
		setInputProps({ ...inputProps, value });
		updatePayload(value);
		validateSchema(value);
	}

	function clearInputField() {
		setEFormFieldError(required && (propertyName));
		setInputProps({ ...inputProps, value: null });
		updatePayload(null);
		setSelectedDate(null);
	}

	function isLongPlaceholder(placeholder) {
		return placeholder && placeholder.length > 30;
	}

	if (schema.format?.includes('date')) {
		const placeholderText = isReadOnly
			? null
			: schema.format.includes("date-time")
				? "Select Date and Time"
				: "Select Date";
		return (
			<div className="eform-input__position-div">
				<ReactDatePicker
					popperClassName="datepick__popper"
					error={error}
					readOnly={isReadOnly}
					disabled={isReadOnly}
					dateFormat="yyyy-MM-dd'T'HH:mm:ssXXX"
					todayButton="Today"
					dropdownMode="select"
					shouldCloseOnSelect
					placeholderText={placeholderText}
					showMonthDropdown
					showYearDropdown
					showMonthYearPicker={schema?.format === "custom:date-time:YYYY-MM" ? true : false}
					showTimeSelect={schema.format.includes("date-time") && schema?.format !== "custom:date-time:YYYY-MM" ? true : false}
					customInput={<CustomInput customInputChange={customInputChange} />}
					selected={selectedDate}
					{...inputProps}
				/>
				{selectedDate && <div className="eform-input__icon__close__style" data-testid="eform-custom-date-picker-close" onClick={() => clearInputField()} >
					<IconCloseGrey />
				</div>}
			</div>
		);
	}

	return schema.enum ? (
		<Dropdown
			placeholder={isReadOnly ? null : schema.description || schema.title}
			value={inputProps?.value}
			disabled={isReadOnly}
		>
			{options().map(({ value, index }) => {
				return (
					<Dropdown.Item key={index}
						id={value}
						onClick={() => {
							onClick(value)
						}}
					>
						{value}
					</Dropdown.Item>
				);
			})}
		</Dropdown>
	) : (
		<InputWithTooltipPlaceholder
			{...inputProps}
			onChange={onChange}
			className={getClassName(error)}
		/>
	);
};


CellStringComponent.propTypes = {
	error: PropTypes.bool,
	isReadOnly: PropTypes.bool,
	schema: PropTypes.object,
	payload: PropTypes.object,
	parentProps: PropTypes.array,
	inputProps: PropTypes.object,
	setInputProps: PropTypes.func,
	updatePayload: PropTypes.func,
	validateSchema: PropTypes.func,
	getFieldValue: PropTypes.func,
	setEFormFieldError: PropTypes.func,
	setIsValidFormat: PropTypes.func,
	required: PropTypes.bool,
	propertyName: PropTypes.string
};

CustomInput.propTypes = {
	value: PropTypes.string,
	error: PropTypes.bool,
	disabled: PropTypes.bool,
	className: PropTypes.object,
	customInputChange: PropTypes.func.isRequired,
	placeholder: PropTypes.string
};
export default CellStringComponent;
